import React, { useEffect, useState, useCallback, useMemo } from "react";
import { formatBRL, formatDateTimeBR } from "../lib/api";
import { fetchWithV2Fallback } from "../lib/v2";
import { AlertTriangle, Clock, RefreshCw, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import StatusBadge from "../components/StatusBadge";

const TITLE_STATUS_MAP = {
  WAITING_PAYMENT: "pendente",
  PARTIALLY_PAID: "aprovada",
  PAID: "liquidada",
  OVERDUE: "vencida",
  WRITTEN_OFF: "cancelada",
  CANCELLED: "cancelada",
};

export default function APagar() {
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWithV2Fallback("/v2/titles", null);
      const list = result.data?.items || (Array.isArray(result.data) ? result.data : []);
      setTitles(list);
    } catch {
      setError("Erro ao carregar titulos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const in30 = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().split("T")[0]; }, []);

  const pending = useMemo(() => titles.filter(t => ["OVERDUE", "WAITING_PAYMENT", "PARTIALLY_PAID"].includes(t.status)), [titles]);

  const overdue = useMemo(() => pending.filter(t => t.status === "OVERDUE" || (t.due_date && t.due_date < today)), [pending, today]);
  const dueToday = useMemo(() => pending.filter(t => t.due_date === today), [pending, today]);
  const dueNext30 = useMemo(() => pending.filter(t => t.due_date > today && t.due_date <= in30), [pending, today, in30]);

  // Vocabulário canônico da V2 (PUBLIC_ALIASES) como fallback do nome legado, seleção por `??`
  // (nullish) e NUNCA por `||` — um `0` canônico legítimo não pode ser descartado. Na prática,
  // `current_outstanding_balance_brl` é obrigatório (sempre string truthy) no caminho GET
  // /v2/titles, então o ramo `(t.net_present_liquid_value ?? t.liquid_value)` é hoje praticamente
  // inalcançável — isto é defesa-em-profundidade para uma fonte alternativa/futura, sem efeito
  // observável no caminho atual.
  const sumBalance = (list) => list.reduce((s, t) => s + parseFloat(t.current_outstanding_balance_brl || (t.net_present_liquid_value ?? t.liquid_value) || 0), 0);
  const totalOverdue = useMemo(() => sumBalance(overdue), [overdue]);
  const totalToday = useMemo(() => sumBalance(dueToday), [dueToday]);
  const totalNext30 = useMemo(() => sumBalance(dueNext30), [dueNext30]);

  const allActive = useMemo(() => [...overdue, ...dueToday, ...dueNext30].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")), [overdue, dueToday, dueNext30]);

  return (
    <div className="space-y-6" data-testid="apagar-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Financeiro</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">A Pagar</h1>
          <p className="mt-1 text-sm text-zinc-500">Visao da saude da carteira — titulos vencidos, a vencer e liquidados.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="apagar-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="zemo-card p-5 border-l-4 border-l-red-500" data-testid="card-overdue">
          <div className="flex items-center gap-2 text-sm text-red-700 font-medium"><AlertTriangle className="h-4 w-4" /> Vencidos</div>
          <div className="mt-2 text-2xl font-bold font-mono-num text-red-900">{formatBRL(totalOverdue)}</div>
          <div className="text-[12px] text-red-600 mt-1">{overdue.length} titulo(s)</div>
        </div>
        <div className="zemo-card p-5 border-l-4 border-l-amber-500" data-testid="card-today">
          <div className="flex items-center gap-2 text-sm text-amber-700 font-medium"><Clock className="h-4 w-4" /> Vencendo hoje</div>
          <div className="mt-2 text-2xl font-bold font-mono-num text-amber-900">{formatBRL(totalToday)}</div>
          <div className="text-[12px] text-amber-600 mt-1">{dueToday.length} titulo(s)</div>
        </div>
        <div className="zemo-card p-5 border-l-4 border-l-blue-500" data-testid="card-next30">
          <div className="flex items-center gap-2 text-sm text-blue-700 font-medium"><Clock className="h-4 w-4" /> Proximos 30 dias</div>
          <div className="mt-2 text-2xl font-bold font-mono-num text-blue-900">{formatBRL(totalNext30)}</div>
          <div className="text-[12px] text-blue-600 mt-1">{dueNext30.length} titulo(s)</div>
        </div>
      </div>

      {loading ? <Skeleton className="h-64 rounded-lg" /> : error ? (
        <div className="zemo-card p-8 text-center text-red-600">{error}</div>
      ) : allActive.length === 0 ? (
        <div className="zemo-card p-12 text-center" data-testid="apagar-empty">
          <CheckCircle className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
          <p className="text-zinc-500">Nenhum titulo pendente</p>
          <p className="text-xs text-zinc-400 mt-1">Todos os titulos estao liquidados ou nao ha titulos registrados.</p>
        </div>
      ) : (
        <div className="zemo-card overflow-hidden">
          <div className="px-5 py-3 border-b border-[var(--zemo-border)] bg-zinc-50/60">
            <h3 className="text-sm font-semibold text-zinc-700">Titulos pendentes ({allActive.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/30 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[var(--zemo-border)]">
                <tr>
                  <th className="text-left px-5 py-2.5 font-semibold">ID Externo</th>
                  <th className="text-right px-5 py-2.5 font-semibold">Valor Face</th>
                  <th className="text-right px-5 py-2.5 font-semibold">Valor Liquido</th>
                  <th className="text-right px-5 py-2.5 font-semibold">Pago</th>
                  <th className="text-right px-5 py-2.5 font-semibold">Saldo Devedor</th>
                  <th className="text-left px-5 py-2.5 font-semibold">Vencimento</th>
                  <th className="text-left px-5 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--zemo-border)]" data-testid="apagar-table">
                {allActive.map((t, i) => {
                  const portalStatus = TITLE_STATUS_MAP[t.status] || (t.status || "").toLowerCase();
                  const isOverdue = t.status === "OVERDUE";
                  return (
                    <tr key={t.id || i} className={`hover:bg-zinc-50/60 ${isOverdue ? "bg-red-50/30" : ""}`} data-testid={`apagar-row-${i}`}>
                      <td className="px-5 py-3.5 font-mono-num font-semibold text-zinc-900">{t.external_id || "--"}</td>
                      <td className="px-5 py-3.5 text-right font-mono-num">{formatBRL(parseFloat(t.gross_future_value ?? (t.gross_face_value || 0)))}</td>
                      <td className="px-5 py-3.5 text-right font-mono-num">{formatBRL(parseFloat(t.net_present_liquid_value ?? (t.liquid_value || 0)))}</td>
                      <td className="px-5 py-3.5 text-right font-mono-num text-emerald-700">{formatBRL(parseFloat(t.cumulative_payment_brl || 0))}</td>
                      <td className={`px-5 py-3.5 text-right font-mono-num font-semibold ${isOverdue ? "text-red-700" : "text-zinc-900"}`}>
                        {formatBRL(parseFloat(t.current_outstanding_balance_brl || 0))}
                      </td>
                      <td className={`px-5 py-3.5 font-mono-num ${isOverdue ? "text-red-600 font-semibold" : "text-zinc-500"}`}>{t.due_date || "--"}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={portalStatus} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
